import { createContext, type ReactNode, use, useCallback, useMemo, useState } from "react";
import type { SelfCheckInAvailability } from "@/features/pulse-surveys/schemas/self-check-in.schema";

interface CheckInLauncherContextValue {
  requestToken: number;
  launchCheckIn: () => void;
  isCheckInOpen: boolean;
  setCheckInOpen: (open: boolean) => void;
}

const CheckInLauncherContext = createContext<CheckInLauncherContextValue | null>(null);

interface ShouldOpenCheckInFromLaunchInput {
  requestToken: number;
  handledToken: number;
  isReadOnly: boolean;
  isOverviewLoading: boolean;
  hasOverview: boolean;
  availability: SelfCheckInAvailability | null;
}

export function shouldOpenCheckInFromLaunch(input: ShouldOpenCheckInFromLaunchInput): boolean {
  if (input.requestToken === 0 || input.requestToken === input.handledToken) return false;
  if (input.isReadOnly || input.isOverviewLoading || !input.hasOverview) return false;
  return input.availability !== "done_today";
}

export function CheckInLauncherProvider({ children }: { children: ReactNode }): ReactNode {
  const [requestToken, setRequestToken] = useState(0);
  const [isCheckInOpen, setCheckInOpen] = useState(false);

  const launchCheckIn = useCallback(() => {
    setRequestToken((current) => current + 1);
  }, []);

  const value = useMemo<CheckInLauncherContextValue>(
    () => ({ requestToken, launchCheckIn, isCheckInOpen, setCheckInOpen }),
    [requestToken, launchCheckIn, isCheckInOpen],
  );

  return <CheckInLauncherContext value={value}>{children}</CheckInLauncherContext>;
}

export function useCheckInLauncher(): CheckInLauncherContextValue {
  const context = use(CheckInLauncherContext);
  if (context === null) {
    throw new Error("useCheckInLauncher must be used inside a <CheckInLauncherProvider>");
  }
  return context;
}
